import type { Temperature } from '@hero/schema';

/**
 * Formats a temperature degree range as a human-readable string.
 *
 * @returns e.g. `"-18 to -15°C"`, or `null` when dry / no meaningful degree.
 */
export function formatTemperatureDegree(temperature: Temperature): string | null {
  const isDry =
    temperature.types?.includes('dry') || temperature.types?.[0] === 'dry';
  if (isDry) return null;

  const deg = temperature.degree;
  if (!deg || (deg.min === 0 && deg.max === 0)) return null;

  return `${deg.min} to ${deg.max}°C`;
}

/**
 * Builds a full temperature display string including the type label.
 *
 * @param temperature  The temperature object.
 * @param typeLabel    Translated type name (e.g. "Chilled", "Frozen", "مبرد").
 * @returns e.g. `"Frozen -18 to -15°C"`, or `typeLabel` alone when no degree,
 *          or `null` when dry.
 */
export function formatTemperatureText(
  temperature: Temperature,
  typeLabel: string,
): string | null {
  const isDry =
    temperature.types?.includes('dry') || temperature.types?.[0] === 'dry';
  if (isDry) return null;

  const degree = formatTemperatureDegree(temperature);
  if (!degree) return typeLabel;

  return `${typeLabel} ${degree}`;
}
